import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../store/userStore";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useTheme } from "../../context/ThemeContext";
import {
  AppsSidebar,
  useFilteredCategories,
} from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import type { AppItem } from "../../components/base/AppsSidebar";
import { logoutUser } from "../../pages/login/functions";
import { setLoggingOut } from "../../utils/tokenUtils";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import {
  TRUCK_YARD_INSPECTOR_ROLE_ID,
  TRUCK_YARD_ADMIN_ROLE_ID,
  TRUCK_YARD_GUARD_ROLE_ID,
} from "../../hooks/useTruckYardRole";
import useTruckYardRole, {
  getUserAssignedYardIds,
  getAssignedYardsLabel,
} from "../../hooks/useTruckYardRole";
import { useAccessValidation } from "../../hooks/useAccessValidation";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";
import { useTruckYardStore } from "../../store/truckYardStore";
import Header from "./Header";

export default function InspectionsLayout() {
  const user = useUserStore((state) => state.user);
  const filteredCategories = useFilteredCategories();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isCollapsed: isSidebarCollapsed, toggleCollapsed: toggleSidebar } =
    useSidebarCollapsed("inspectionsSidebarCollapsed", {
      collapseOnSmallScreens: true,
    });

  const companyId = useCompaniesStore((state) => state.companyId);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore(
    (state) => state.fetchAllCompanies,
  );

  const userRoleId = localStorage.getItem("userRoleId") || "";
  const isReseller = userRoleId === ADMIN_RESELLER_ROLE_ID;
  const isPTARRole =
    userRoleId === TRUCK_YARD_INSPECTOR_ROLE_ID ||
    userRoleId === TRUCK_YARD_ADMIN_ROLE_ID ||
    userRoleId === TRUCK_YARD_GUARD_ROLE_ID;

  // Get company name from localStorage for PTAR users
  const storedCompanyName = localStorage.getItem("userCompanyName") || "";

  // Get assigned yard name for supervisors/inspectors (not admins - they can have multiple yards)
  const { hasYardRestrictions } = useTruckYardRole();

  // Continuous access validation for guards and supervisors
  // Checks geofence and schedule periodically
  useAccessValidation({ enabled: true });

  // Un solo campo para los cuatro roles (issue #497): el respaldo al singular
  // que antes se resolvía acá vive ahora dentro de getUserAssignedYardIds.
  const assignedYardIds = getUserAssignedYardIds();

  const yards = useTruckYardStore((state) => state.yards);
  const fetchYards = useTruckYardStore((state) => state.fetchYards);
  const [assignedYardName, setAssignedYardName] = useState("");

  // Fetch yards and get assigned yard name (only for users with yard restrictions)
  useEffect(() => {
    // Este `length > 0` NO es de los que apagaban el recorte (issue #497): acá
    // sólo se busca el NOMBRE del patio para el encabezado. Sin patios no hay
    // nombre que mostrar, así que no vale la pena pedir la lista.
    if (hasYardRestrictions() && assignedYardIds.length > 0) {
      fetchYards();
    }
  }, [hasYardRestrictions, assignedYardIds.length, fetchYards]);

  useEffect(() => {
    if (
      hasYardRestrictions() &&
      assignedYardIds.length > 0 &&
      yards.length > 0
    ) {
      // Con varios patios no se nombra uno arbitrario: ver getAssignedYardsLabel.
      setAssignedYardName(getAssignedYardsLabel(assignedYardIds, yards));
    }
  }, [hasYardRestrictions, assignedYardIds, yards]);

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  const hasRedirected = useRef(false);

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);

  // Sincronizar companyId desde localStorage al montar
  useEffect(() => {
    const storedCompanyId = localStorage.getItem("companyId");
    if (storedCompanyId && storedCompanyId !== companyId) {
      setCompanyId(storedCompanyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar todas las compañías para el selector cuando es reseller
  useEffect(() => {
    if (isReseller) {
      fetchAllCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReseller]);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === "inspecciones") {
      closeAppsSidebar();
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  const menuItems = [
    {
      id: "history",
      label: "Historial de Inspección",
      icon: "ri-file-list-line",
      path: "/inspecciones/historial",
    },
  ];

  return (
    <div
      className={`h-screen flex flex-col ${isDarkMode ? "bg-[#1A1A1A]" : "bg-gray-50"}`}
    >
      {/* Banner de sesión por expirar */}
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}

      <Header
        onOpenAppsSidebar={openAppsSidebar}
        onToggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        isDarkMode={isDarkMode}
        isReseller={isReseller}
        companies={allCompanies}
        selectedCompany={companyId}
        onCompanyChange={setCompanyId}
      />

      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        <AppSidebarMenu
          menuItems={menuItems}
          isDarkMode={isDarkMode}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
          onNavigate={(path) => {
            navigate(path);
          }}
          isActiveRoute={(_itemId, path) =>
            location.pathname === path ||
            location.pathname.startsWith(path + "/")
          }
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          companyIndicator={{
            companyId,
            companyName:
              allCompanies.find((c) => c.id === companyId)?.name ||
              allCompanies.find((c) => c.id === companyId)?.business_name ||
              storedCompanyName ||
              companyId,
            yardName: assignedYardName,
            isReseller: isReseller || isPTARRole,
            isDarkMode,
          }}
          footerStats={{
            label: "Resumen",
            items: [{ label: "Total inspecciones", value: 0 }],
          }}
        />

        {/* Main Content */}
        <div className="flex-1 max-h-full overflow-hidden min-w-0 w-full">
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="inspecciones"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
